// PushCampaigns.jsx — BespokeBridge Communications Module
// Sub-tabs: Campaigns | Create/Edit | Templates | Campaign Frequency
// Props: { orgId, adminId, adminName }

import React, { useState, useEffect, useCallback, useRef } from 'react';
import CampaignFrequencyControl from './CampaignFrequencyControl';
import FinalCustomersTargeted from './FinalCustomersTargeted';
import MergeFieldValuesSection from './MergeFieldValuesSection';
import {
  getPushCampaigns,
  createPushCampaign,
  updatePushCampaign,
  sendPushCampaignNow,
  schedulePushCampaign,
  cancelPushCampaign,
  duplicatePushCampaign,
  getPushTemplates,
  createPushTemplate,
  updatePushTemplate,
  seedDefaultTemplates,
  resolveAudience,
  resolveMergeFields,
  fetchDistinctFieldValues,
  getOrgName,
  getStaffDisplayName,
  AUDIENCE_TYPES,
  MEMBER_STATUS_FILTERS,
  PUSH_CHAR_LIMITS,
  getTargetingTypes,
  BIRTH_MONTHS,
  searchContacts,
  fmtDate,
  fmtDateTime,
  getChannelOptInList,
  getContactOptIn,
  updateChannelOptIn,
  exportChannelOptInCSV,
} from '../../../api/communicationsApi';
import { getFinalContactList } from '../../../api/campaignExclusionService';

const PAGE_SIZE = 25;
const PREVIEW_PAGE_SIZE = 100;

const EMOJIS = ['😊','🎉','💙','✨','🌟','🙌','👋','❤️','🏆','📢','🗓️','📋','🤝','💪','🌸'];

const STATUS_STYLES = {
  draft:     { bg: '#F3F4F6', color: '#374151', border: '#D1D5DB' },
  scheduled: { bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
  sent:      { bg: '#D1FAE5', color: '#065F46', border: '#6EE7B7' },
  cancelled: { bg: '#FEE2E2', color: '#991B1B', border: '#FCA5A5' },
  sending:   { bg: '#FEF3C7', color: '#92400E', border: '#FCD34D' },
  failed:    { bg: '#FEE2E2', color: '#991B1B', border: '#FCA5A5' },
};

function emptyForm() {
  return {
    name: '', templateId: '', tplCategory: '',
    title: '', body: '', imageUrl: '', deepLinkUrl: '',
    targetingType: 'all_contacts', audienceFilters: {},
    selectedContacts: [],
    scheduleType: 'now', scheduledAt: '',
    recurring: false,
    recurringFrequency: 'weekly',
    recurringDay: '',
    recurringMonth: '',
    recurringStartDate: '',
    recurringEndDate: '',
    customMergeValues: {},
  };
}

function derivedAudienceType(targetingType) {
  if (['all_members', 'member_status', 'member_birthday', 'member_district'].includes(targetingType)) return 'members';
  if (['all_volunteers', 'volunteer_district', 'volunteer_status'].includes(targetingType)) return 'volunteers';
  return 'both'; // all_contacts / specific_contacts (donors are email-only)
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 700, border: `1px solid ${s.border}`,
      background: s.bg, color: s.color, textTransform: 'capitalize',
    }}>
      {status || 'draft'}
    </span>
  );
}

function AudienceLabel({ audienceType }) {
  const map = { members: 'Members', volunteers: 'Volunteers', both: 'Members + Volunteers', donors: 'Donors', all: 'Members + Volunteers + Donors' };
  return <span>{map[audienceType] || audienceType || '—'}</span>;
}

function CharCounter({ value, limit, label }) {
  const over = value.length > limit;
  return (
    <span style={{ fontSize: 11, color: over ? '#EF4444' : '#9CA3AF', fontWeight: over ? 700 : 400 }}>
      {label}: {value.length}/{limit}{over ? ' — over limit!' : ''}
    </span>
  );
}

// ── Message Preview Pane (mobile / desktop) ───────────────────────────────────
function MessagePreviewPane({ title, body, imageUrl, orgName, previewName, customMergeValues }) {
  const [device, setDevice] = React.useState('mobile');
  if (!title && !body) return null;

  const sampleContact = { displayName: previewName || 'Staff Member' };
  const resolvedOrgName = orgName || '[Org Name]';
  const resolvedTitle = resolveMergeFields(title || '', sampleContact, resolvedOrgName, customMergeValues);
  const resolvedBody  = resolveMergeFields(body  || '', sampleContact, resolvedOrgName, customMergeValues);

  const btnStyle = (key) => ({
    display: 'flex', alignItems: 'center', gap: 5, padding: '4px 12px',
    borderRadius: 20, fontSize: 12, cursor: 'pointer', transition: 'all 0.15s',
    border: `1.5px solid ${device === key ? '#4338CA' : '#D1D5DB'}`,
    background: device === key ? '#EEF2FF' : '#FAFAFA',
    color: device === key ? '#4338CA' : '#9CA3AF',
    fontWeight: device === key ? 700 : 400,
  });

  const NotifCard = ({ small }) => (
    <div style={{ background: 'rgba(245,245,250,0.96)', borderRadius: 14, padding: small ? '8px 10px' : '12px 14px', boxShadow: '0 2px 12px rgba(0,0,0,0.15)', maxWidth: small ? '100%' : 320 }}>
      {imageUrl && (
        <div style={{ borderRadius: 10, overflow: 'hidden', marginBottom: small ? 6 : 8, background: '#E5E7EB', height: small ? 60 : 120, width: '100%' }}>
          <img src={imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }} onError={e => { e.target.parentElement.style.display = 'none'; }} />
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: small ? 6 : 8 }}>
        <div style={{ width: small ? 22 : 28, height: small ? 22 : 28, borderRadius: 6, background: '#4338CA', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span style={{ color: '#fff', fontSize: small ? 7 : 9, fontWeight: 700 }}>BB</span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: small ? 8.5 : 11, color: '#111', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {resolvedTitle || 'Notification Title'}
          </div>
          <div style={{ fontSize: small ? 7.5 : 10, color: '#555', lineHeight: 1.4 }}>
            {resolvedBody || 'Notification body text will appear here.'}
          </div>
          <div style={{ fontSize: small ? 7 : 9, color: '#999', marginTop: 2 }}>BespokeBridge · now</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bb-comm-preview-panel" style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span className="bb-comm-preview-label" style={{ margin: 0 }}>Message Preview</span>
        <button type="button" style={btnStyle('mobile')} onClick={() => setDevice('mobile')}>
          <svg width="10" height="14" viewBox="0 0 10 14" fill="none"><rect x=".5" y=".5" width="9" height="13" rx="1.5" stroke="currentColor" strokeWidth="1"/><rect x="3.5" y="11" width="3" height="1" rx=".5" fill="currentColor"/></svg>
          Mobile
        </button>
        <button type="button" style={btnStyle('desktop')} onClick={() => setDevice('desktop')}>
          <svg width="14" height="11" viewBox="0 0 14 11" fill="none"><rect x=".5" y=".5" width="13" height="8" rx="1" stroke="currentColor" strokeWidth="1"/><rect x="2" y="2" width="10" height="5.5" rx=".3" fill="currentColor" opacity=".18"/><rect x="0" y="9" width="14" height="1.5" rx=".75" fill="currentColor" opacity=".5"/></svg>
          Desktop
        </button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', overflowX: 'auto' }}>
        {device === 'mobile' ? (
          <div style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}>
            <div style={{ position: 'absolute', left: -3, top: 58, width: 3, height: 11, background: '#3a3a3a', borderRadius: '2px 0 0 2px' }} />
            <div style={{ position: 'absolute', left: -3, top: 76, width: 3, height: 20, background: '#3a3a3a', borderRadius: '2px 0 0 2px' }} />
            <div style={{ position: 'absolute', left: -3, top: 102, width: 3, height: 20, background: '#3a3a3a', borderRadius: '2px 0 0 2px' }} />
            <div style={{ position: 'absolute', right: -3, top: 82, width: 3, height: 28, background: '#3a3a3a', borderRadius: '0 2px 2px 0' }} />
            <div style={{ width: 224, background: 'linear-gradient(170deg,#2d2d2d 0%,#1a1a1a 50%,#232323 100%)', borderRadius: 22, overflow: 'hidden', boxShadow: '0 0 0 1px rgba(255,255,255,0.06), 0 22px 55px rgba(0,0,0,0.65)' }}>
              <div style={{ background: '#000', paddingTop: 10, paddingBottom: 5, display: 'flex', justifyContent: 'center' }}>
                <div style={{ width: 50, height: 14, background: '#0d0d0d', borderRadius: 10 }} />
              </div>
              <div style={{ background: 'linear-gradient(135deg,#1a1a2e,#16213e)', padding: 10, minHeight: 362, fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', textAlign: 'center', marginBottom: 8 }}>9:41 AM</div>
                <NotifCard small />
              </div>
              <div style={{ background: '#000', paddingTop: 6, paddingBottom: 12, display: 'flex', justifyContent: 'center' }}>
                <div style={{ width: 40, height: 4, background: 'rgba(255,255,255,0.22)', borderRadius: 3 }} />
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'inline-block', flexShrink: 0 }}>
            <div style={{ width: 483, background: '#1c1c1e', borderRadius: '8px 8px 0 0', padding: '8px 7px 0' }}>
              <div style={{ width: 4, height: 4, background: '#444', borderRadius: '50%', margin: '0 auto 4px' }} />
              <div style={{ background: '#2a2a2e', borderRadius: '3px 3px 0 0', minHeight: 276, padding: 16, fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 12 }}>Desktop notification</div>
                <NotifCard small={false} />
              </div>
            </div>
            <div style={{ width: 483, height: 10, background: '#2c2c2e' }} />
            <div style={{ width: 515, height: 8, background: '#1c1c1e', borderRadius: '0 0 6px 6px', marginLeft: -16, boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }} />
          </div>
        )}
      </div>
      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 10, textAlign: 'center' }}>
        Preview of how the push notification will appear. {device === 'mobile' ? 'Mobile' : 'Desktop'} view.
      </div>
    </div>
  );
}

// ── Emoji Picker ──────────────────────────────────────────────────────────────
function EmojiPicker({ onSelect, onClose }) {
  return (
    <div style={{
      position: 'absolute', zIndex: 200, background: '#fff', border: '1px solid #E5E7EB',
      borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', padding: 12, top: 36, left: 0,
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 4, marginBottom: 8 }}>
        {EMOJIS.map(e => (
          <button key={e} type="button" style={{ fontSize: 20, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 6 }}
            onMouseOver={ev => ev.currentTarget.style.background = '#F3F4F6'}
            onMouseOut={ev => ev.currentTarget.style.background = 'none'}
            onClick={() => { onSelect(e); onClose(); }}
          >{e}</button>
        ))}
      </div>
      <button type="button" style={{ fontSize: 11, color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer', display: 'block', margin: '0 auto' }} onClick={onClose}>Close</button>
    </div>
  );
}

// ── Recipient Preview Modal ───────────────────────────────────────────────────
function RecipientPreviewModal({ orgId, targetingType, audienceFilters, selectedContacts, onClose }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [previewPage, setPreviewPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const at = derivedAudienceType(targetingType);
    const filters = targetingType === 'specific_contacts'
      ? { specificContactIds: (selectedContacts || []).map(c => c.id) }
      : audienceFilters;
    resolveAudience(orgId, at, filters, 'push')
      .then(r => { if (!cancelled) { setContacts(r.contacts || []); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orgId, targetingType, audienceFilters, selectedContacts]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const filtered = contacts.filter(c => {
    if (!filterText) return true;
    const q = filterText.toLowerCase();
    return (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    let va = a[sortKey] || ''; let vb = b[sortKey] || '';
    if (typeof va === 'string') va = va.toLowerCase();
    if (typeof vb === 'string') vb = vb.toLowerCase();
    return sortDir === 'asc' ? (va < vb ? -1 : va > vb ? 1 : 0) : (va > vb ? -1 : va < vb ? 1 : 0);
  });

  const totalPreviewPages = Math.max(1, Math.ceil(sorted.length / PREVIEW_PAGE_SIZE));
  const paged = sorted.slice((previewPage - 1) * PREVIEW_PAGE_SIZE, previewPage * PREVIEW_PAGE_SIZE);

  const exportCSV = () => {
    const rows = [['Name','Type','Email','FCM Token']];
    sorted.forEach(c => rows.push([c.name||'', c.contactType||'', c.email||'', c.fcmToken ? 'Yes' : 'No']));
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'push_recipients.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const SortIcon = ({ k }) => sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅';

  return (
    <div className="bb-comm-modal-overlay">
      <div className="bb-comm-modal bb-comm-modal--wide" style={{ maxWidth: 700 }}>
        <div className="bb-comm-modal-header">
          <h4 className="bb-comm-modal-title">Recipient Preview — Push Campaign</h4>
          <button className="bb-comm-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="bb-comm-modal-body">
          <div className="bb-comm-preview-stats">
            <div className="bb-comm-preview-stat">
              <div className="bb-comm-preview-stat-val">{loading ? '…' : contacts.length}</div>
              <div className="bb-comm-preview-stat-lbl">Total in Segment</div>
            </div>
            <div className="bb-comm-preview-stat">
              <div className="bb-comm-preview-stat-val">{loading ? '…' : contacts.filter(c => c.fcmToken).length}</div>
              <div className="bb-comm-preview-stat-lbl">FCM-Eligible</div>
            </div>
            <div className="bb-comm-preview-stat">
              <div className="bb-comm-preview-stat-val">{loading ? '…' : contacts.filter(c => (c._type || c.contactType) === 'member').length}</div>
              <div className="bb-comm-preview-stat-lbl">Members</div>
            </div>
            <div className="bb-comm-preview-stat">
              <div className="bb-comm-preview-stat-val">{loading ? '…' : contacts.filter(c => (c._type || c.contactType) === 'volunteer').length}</div>
              <div className="bb-comm-preview-stat-lbl">Volunteers</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'center' }}>
            <input
              className="bb-form-input"
              style={{ flex: 1 }}
              placeholder="Filter by name or email…"
              value={filterText}
              onChange={e => { setFilterText(e.target.value); setPreviewPage(1); }}
            />
            <button className="bb-btn-secondary" onClick={exportCSV} disabled={loading || sorted.length === 0}>Export CSV</button>
          </div>
          {loading ? (
            <div className="bb-comm-loading">Loading recipients…</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto', maxHeight: 340, overflowY: 'auto' }}>
                <table className="bb-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('name')}>Name<SortIcon k="name" /></th>
                      <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('_type')}>Type<SortIcon k="_type" /></th>
                      <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('email')}>Email<SortIcon k="email" /></th>
                      <th>FCM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.length === 0 ? (
                      <tr><td colSpan={4} className="bb-comm-empty-row">No recipients found.</td></tr>
                    ) : paged.map((c, i) => (
                      <tr key={c.id || i}>
                        <td className="bb-comm-cell--name">{c.name || '—'}</td>
                        <td><span style={{ textTransform: 'capitalize', fontSize: 12, color: (c._type || c.contactType) === 'member' ? '#0D9488' : '#4338CA', fontWeight: 600 }}>{c._type || c.contactType || '—'}</span></td>
                        <td className="bb-comm-cell--muted">{c.email || '—'}</td>
                        <td>{c.fcmToken ? <span style={{ color: '#10B981', fontSize: 12, fontWeight: 600 }}>✓ Yes</span> : <span style={{ color: '#9CA3AF', fontSize: 12 }}>No</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPreviewPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 12, fontSize: 13 }}>
                  <button className="bb-btn-secondary" onClick={() => setPreviewPage(p => Math.max(1, p - 1))} disabled={previewPage === 1}>Prev</button>
                  <span style={{ color: '#6B7280' }}>Page {previewPage} of {totalPreviewPages} ({sorted.length} total)</span>
                  <button className="bb-btn-secondary" onClick={() => setPreviewPage(p => Math.min(totalPreviewPages, p + 1))} disabled={previewPage === totalPreviewPages}>Next</button>
                </div>
              )}
            </>
          )}
        </div>
        <div className="bb-comm-modal-footer">
          <button className="bb-btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Audience Filters Section ──────────────────────────────────────────────────
function AudienceFiltersSection({ targetingType, audienceFilters, selectedContacts, contactSearch, contactResults, searchingContacts, onTargetingChange, onFiltersChange, onContactSearch, onAddContact, onRemoveContact, memberStatuses, memberDistricts, volunteerStatuses, volunteerDistricts }) {
  const [contactInputFocused, setContactInputFocused] = React.useState(false);
  const toggle = (key, val) => {
    const cur = audienceFilters[key] || [];
    const next = cur.includes(val) ? cur.filter(v => v !== val) : [...cur, val];
    onFiltersChange({ ...audienceFilters, [key]: next });
  };

  return (
    <div>
      <div className="bb-form-group">
        <label className="bb-form-label">Target Audience</label>
        <select className="bb-form-select" value={targetingType} onChange={e => onTargetingChange(e.target.value)}>
          {getTargetingTypes('push').map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      {targetingType === 'member_status' && (
        <div className="bb-comm-audience-filters">
          <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>Member Status</div>
          <div className="bb-comm-checkbox-group">
            {(memberStatuses.length > 0 ? memberStatuses : MEMBER_STATUS_FILTERS.map(f => f.value)).map(val => (
              <label key={val} className="bb-comm-checkbox-label">
                <input type="checkbox" checked={(audienceFilters.memberStatus || []).includes(val)} onChange={() => toggle('memberStatus', val)} />
                {val}
              </label>
            ))}
          </div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>Leave all unchecked to include all member statuses.</div>
        </div>
      )}

      {targetingType === 'member_district' && (
        <div className="bb-comm-audience-filters">
          <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>Member District</div>
          <select
            className="bb-form-select"
            style={{ maxWidth: 280 }}
            value={audienceFilters.memberDistrict || ''}
            onChange={e => onFiltersChange({ ...audienceFilters, memberDistrict: e.target.value })}
          >
            <option value="">— Select district —</option>
            {memberDistricts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      )}

      {targetingType === 'member_birthday' && (
        <div className="bb-comm-audience-filters">
          <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>Birthday Month(s)</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px 12px' }}>
            {BIRTH_MONTHS.map(m => (
              <label key={m} className="bb-comm-checkbox-label">
                <input type="checkbox" checked={(audienceFilters.birthMonths || []).includes(m)} onChange={() => toggle('birthMonths', m)} />
                {m}
              </label>
            ))}
          </div>
        </div>
      )}

      {targetingType === 'volunteer_status' && (
        <div className="bb-comm-audience-filters">
          <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>Volunteer Status</div>
          <div className="bb-comm-checkbox-group">
            {(volunteerStatuses.length > 0 ? volunteerStatuses : ['active', 'inactive']).map(val => (
              <label key={val} className="bb-comm-checkbox-label">
                <input type="checkbox" checked={(audienceFilters.volunteerStatus || []).includes(val)} onChange={() => toggle('volunteerStatus', val)} />
                {val}
              </label>
            ))}
          </div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>Leave all unchecked to include all volunteer statuses.</div>
        </div>
      )}

      {targetingType === 'volunteer_district' && (
        <div className="bb-comm-audience-filters">
          <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>Volunteer District</div>
          <select
            className="bb-form-select"
            style={{ maxWidth: 280 }}
            value={audienceFilters.volunteerDistrict || ''}
            onChange={e => onFiltersChange({ ...audienceFilters, volunteerDistrict: e.target.value })}
          >
            <option value="">— Select district —</option>
            {volunteerDistricts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      )}

      {targetingType === 'specific_contacts' && (
        <div className="bb-comm-audience-filters">
          <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>Search & Add Contacts</div>
          <div style={{ position: 'relative' }}>
            <input
              className="bb-form-input"
              placeholder="Search by name or email…"
              value={contactSearch}
              onChange={e => onContactSearch(e.target.value)}
              autoComplete="off"
              onFocus={() => setContactInputFocused(true)}
              onBlur={() => setTimeout(() => setContactInputFocused(false), 150)}
            />
            {searchingContacts && <div style={{ position: 'absolute', right: 10, top: 10, fontSize: 11, color: '#9CA3AF' }}>Searching…</div>}
            {contactInputFocused && contactResults.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.10)', maxHeight: 200, overflowY: 'auto' }}>
                {contactResults.map(c => (
                  <div key={c.id} style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #F3F4F6', fontSize: 13 }}
                    onMouseOver={e => e.currentTarget.style.background = '#F9FAFB'}
                    onMouseOut={e => e.currentTarget.style.background = ''}
                    onClick={() => onAddContact(c)}
                  >
                    <div>
                      <span style={{ fontWeight: 600 }}>{c.name}</span>
                      <span style={{ color: '#9CA3AF', marginLeft: 8 }}>{c.email}</span>
                    </div>
                    <span style={{ fontSize: 11, color: c.contactType === 'member' ? '#0D9488' : '#4338CA', textTransform: 'capitalize', fontWeight: 600 }}>{c.contactType}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {selectedContacts.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {selectedContacts.map(c => (
                <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', borderRadius: 20, padding: '3px 10px', fontSize: 12 }}>
                  {c.name}
                  <button type="button" onClick={() => onRemoveContact(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#93C5FD', fontWeight: 700, fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Recurring Section ─────────────────────────────────────────────────────────
function RecurringSection({ form, onChange }) {
  const set = (k, v) => onChange({ ...form, [k]: v });
  if (!form.recurring) return null;
  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #F3F4F6' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
        <div className="bb-form-group">
          <label className="bb-form-label">Frequency</label>
          <select className="bb-form-select" value={form.recurringFrequency} onChange={e => set('recurringFrequency', e.target.value)}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="annually">Annually</option>
          </select>
        </div>
        {form.recurringFrequency === 'weekly' && (
          <div className="bb-form-group">
            <label className="bb-form-label">Day of Week</label>
            <select className="bb-form-select" value={form.recurringDay} onChange={e => set('recurringDay', e.target.value)}>
              <option value="">— Select day —</option>
              {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        )}
        {form.recurringFrequency === 'monthly' && (
          <div className="bb-form-group">
            <label className="bb-form-label">Day of Month</label>
            <input type="number" className="bb-form-input" min={1} max={28} value={form.recurringDay} onChange={e => set('recurringDay', e.target.value)} placeholder="e.g. 1" />
          </div>
        )}
        {form.recurringFrequency === 'annually' && (
          <div className="bb-form-group">
            <label className="bb-form-label">Month</label>
            <select className="bb-form-select" value={form.recurringMonth} onChange={e => set('recurringMonth', e.target.value)}>
              <option value="">— Select month —</option>
              {BIRTH_MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
        <div className="bb-form-group">
          <label className="bb-form-label">Start Date</label>
          <input type="date" className="bb-form-input" value={form.recurringStartDate} onChange={e => set('recurringStartDate', e.target.value)} />
        </div>
        <div className="bb-form-group">
          <label className="bb-form-label">End Date <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(optional)</span></label>
          <input type="date" className="bb-form-input" value={form.recurringEndDate} onChange={e => set('recurringEndDate', e.target.value)} />
        </div>
      </div>
    </div>
  );
}

// ── Send Confirmation Modal ───────────────────────────────────────────────────
function SendConfirmModal({ name, title, recipientCount, scheduleMode, scheduleAt, onConfirm, onClose, sending }) {
  return (
    <div className="bb-comm-modal-overlay">
      <div className="bb-comm-modal">
        <div className="bb-comm-modal-header">
          <h4 className="bb-comm-modal-title">{scheduleMode ? 'Schedule Push Campaign' : 'Send Push Campaign Now'}</h4>
          <button className="bb-comm-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="bb-comm-modal-body">
          <div className="bb-comm-detail-grid">
            {[
              ['Campaign', name],
              ['Notification Title', title],
              ['FCM-Eligible Recipients', <span style={{ fontWeight: 700, color: '#4338CA', fontSize: 16 }}>{recipientCount}</span>],
              [scheduleMode ? 'Scheduled For' : 'Send Time',
                scheduleMode && scheduleAt
                  ? new Date(scheduleAt).toLocaleString('en-SG', { timeZone: 'Asia/Singapore', dateStyle: 'medium', timeStyle: 'short' })
                  : 'Immediately'],
            ].map(([lbl, val]) => (
              <div key={lbl} className="bb-comm-detail-row">
                <span className="bb-comm-detail-label">{lbl}</span>
                <span style={{ color: '#374151', fontSize: 13 }}>{val}</span>
              </div>
            ))}
          </div>
          {recipientCount === 0 && (
            <div className="bb-comm-warning-box">
              No FCM-eligible recipients found. Ensure your audience members have the app installed with push notifications enabled.
            </div>
          )}
        </div>
        <div className="bb-comm-modal-footer">
          <button className="bb-btn-secondary" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="bb-btn-primary" onClick={onConfirm} disabled={sending || recipientCount === 0}>
            {sending ? 'Working…' : scheduleMode ? 'Schedule' : 'Send Now'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── View Campaign Modal ───────────────────────────────────────────────────────
function ViewModal({ campaign, onClose }) {
  if (!campaign) return null;
  return (
    <div className="bb-comm-modal-overlay">
      <div className="bb-comm-modal bb-comm-modal--wide">
        <div className="bb-comm-modal-header">
          <h4 className="bb-comm-modal-title">Campaign Details</h4>
          <button className="bb-comm-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="bb-comm-modal-body">
          <div className="bb-comm-detail-grid">
            {[
              ['Name',          campaign.name],
              ['Status',        <StatusBadge status={campaign.status} />],
              ['Notif. Title',  campaign.title || '—'],
              ['Audience',      <AudienceLabel audienceType={campaign.audienceType} />],
              ['Recipients',    campaign.recipientCount || '—'],
              ['Deep Link',     campaign.deepLinkUrl || '—'],
              ['Scheduled',     fmtDateTime(campaign.scheduledAt)],
              ['Sent',          fmtDateTime(campaign.sentAt)],
              ['Created By',    campaign.createdBy || '—'],
              ['Created',       fmtDate(campaign.createdAt)],
            ].map(([lbl, val]) => (
              <div key={lbl} className="bb-comm-detail-row">
                <span className="bb-comm-detail-label">{lbl}</span>
                <span style={{ color: '#374151', fontSize: 13 }}>{val}</span>
              </div>
            ))}
          </div>
          {campaign.failureReason && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginTop: 12 }}>
              {campaign.failureReason}
            </div>
          )}
          {Array.isArray(campaign.deliveryResults) && campaign.deliveryResults.length > 0 && (
            <div className="bb-comm-detail-body">
              <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '14px 0 8px' }}>
                Delivery results — {campaign.successCount || 0} delivered, {campaign.failureCount || 0} failed, {campaign.noDeviceCount || 0} no device
                {campaign.staleTokensRemoved ? `, ${campaign.staleTokensRemoved} expired token(s) cleared` : ''}
              </div>
              <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 8 }}>
                <table className="bb-comm-table" style={{ width: '100%', fontSize: 12 }}>
                  <thead><tr><th>Recipient</th><th>Result</th><th>Reason</th></tr></thead>
                  <tbody>
                    {campaign.deliveryResults.map((r, i) => (
                      <tr key={i}>
                        <td>{r.name}{r.email ? <div style={{ color: '#9CA3AF' }}>{r.email}</div> : null}</td>
                        <td style={{ color: r.status === 'sent' ? '#10B981' : '#B45309', fontWeight: 600 }}>{r.status === 'no_device' ? 'No device' : r.status === 'sent' ? 'Delivered' : r.status === 'skipped' ? 'Skipped' : 'Failed'}</td>
                        <td>{r.reason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {campaign.body && (
            <div className="bb-comm-detail-body">
              <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Body</div>
              <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: '14px 16px', fontSize: 13, color: '#374151' }}>
                {campaign.body}
              </div>
            </div>
          )}
        </div>
        <div className="bb-comm-modal-footer">
          <button className="bb-btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Template Editor Form ──────────────────────────────────────────────────────
function TemplateEditorForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    name:           initial?.name           || '',
    category:       initial?.category       || 'triggered',
    triggerContext: initial?.triggerContext || '',
    audience:       initial?.audience       || 'members',
    title:          initial?.title          || '',
    body:           initial?.body           || '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="bb-comm-template-editor">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="bb-form-group">
          <label className="bb-form-label">Template Name *</label>
          <input className="bb-form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Event Registration Confirmation" />
        </div>
        <div className="bb-form-group">
          <label className="bb-form-label">Campaign Type</label>
          <select className="bb-form-select" value={form.category} onChange={e => set('category', e.target.value)}>
            <option value="triggered">Triggered Campaign</option>
            <option value="adhoc">AdHoc Campaign</option>
          </select>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="bb-form-group">
          <label className="bb-form-label">Trigger Context</label>
          <input className="bb-form-input" value={form.triggerContext} onChange={e => set('triggerContext', e.target.value)} placeholder="e.g. Shift management" />
        </div>
        <div className="bb-form-group">
          <label className="bb-form-label">Audience</label>
          <select className="bb-form-select" value={form.audience} onChange={e => set('audience', e.target.value)}>
            {AUDIENCE_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </div>
      </div>
      <div className="bb-form-group">
        <label className="bb-form-label">Notification Title * <span style={{ color: '#9CA3AF', fontWeight: 400, fontSize: 11 }}>(max {PUSH_CHAR_LIMITS.title} chars)</span></label>
        <input
          className="bb-form-input"
          value={form.title}
          onChange={e => set('title', e.target.value)}
          placeholder="e.g. New Shift Available"
          maxLength={PUSH_CHAR_LIMITS.title + 10}
        />
        <CharCounter value={form.title} limit={PUSH_CHAR_LIMITS.title} label="Title" />
      </div>
      <div className="bb-form-group">
        <label className="bb-form-label">Notification Body *</label>
        <textarea
          className="bb-form-input"
          style={{ minHeight: 80, fontFamily: 'inherit', fontSize: 13 }}
          value={form.body}
          onChange={e => set('body', e.target.value)}
          placeholder="e.g. A volunteer shift on {{shiftDate}} just opened. Sign up now!"
        />
        <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
          <CharCounter value={form.body} limit={PUSH_CHAR_LIMITS.bodyIOS} label="iOS" />
          <CharCounter value={form.body} limit={PUSH_CHAR_LIMITS.bodyAndroid} label="Android" />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="bb-btn-primary" onClick={() => onSave(form)} disabled={saving || !form.name || !form.title || !form.body}>
          {saving ? 'Saving…' : 'Save Template'}
        </button>
        <button className="bb-btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
export default function PushCampaigns({ orgId, adminId, adminName }) {
  const [activeTab, setActiveTab] = useState('create');

  // ── Campaigns tab ─────────────────────────────────────────────────────────
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [viewCampaign, setViewCampaign] = useState(null);
  const [toast, setToast] = useState(null);

  // ── Create/Edit tab ───────────────────────────────────────────────────────
  const [editingId, setEditingId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [previewOrgName, setPreviewOrgName]     = useState('');
  const [previewStaffName, setPreviewStaffName] = useState(adminName || '');
  const [memberStatuses, setMemberStatuses]       = useState([]);
  const [memberDistricts, setMemberDistricts]     = useState([]);
  const [volunteerStatuses, setVolunteerStatuses] = useState([]);
  const [volunteerDistricts, setVolunteerDistricts] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [audienceResult, setAudienceResult] = useState(null);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [savedTargeting, setSavedTargeting] = useState(null);
  const [formSaving, setFormSaving] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [sendAction, setSendAction] = useState('now');
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [showRecipientPreview, setShowRecipientPreview] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [searchingContacts, setSearchingContacts] = useState(false);
  const [showTitleEmoji, setShowTitleEmoji] = useState(false);
  const [showBodyEmoji, setShowBodyEmoji] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [imageError, setImageError] = useState('');
  const titleRef = useRef(null);
  const bodyRef = useRef(null);

  // ── Opt-In Management tab ────────────────────────────────────────────────
  const [optInContacts, setOptInContacts]     = useState([]);
  const [loadingOptIn, setLoadingOptIn]       = useState(false);
  const [optInFilter, setOptInFilter]         = useState('');
  const [optInSearch, setOptInSearch]         = useState('');
  const [optInPage, setOptInPage]             = useState(1);
  const [updatingOptIn, setUpdatingOptIn]     = useState(null);
  const [optInReason, setOptInReason]         = useState('');
  const [showOptInReasonFor, setShowOptInReasonFor] = useState(null);

  // ── Templates tab ─────────────────────────────────────────────────────────
  const [tplEditingId, setTplEditingId] = useState(null);
  const [tplSaving, setTplSaving] = useState(false);
  const [tplCategoryFilter, setTplCategoryFilter] = useState('all');

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── Data loading ──────────────────────────────────────────────────────────
  const loadCampaigns = useCallback(async () => {
    setLoadingCampaigns(true);
    try {
      setCampaigns(await getPushCampaigns(orgId));
    } catch (err) {
      showToast('Failed to load campaigns: ' + err.message, 'error');
    } finally {
      setLoadingCampaigns(false);
    }
  }, [orgId]);

  const loadTemplates = useCallback(async () => {
    try {
      setTemplates(await getPushTemplates(orgId));
    } catch (err) {
      console.error('loadTemplates:', err);
    }
  }, [orgId]);

  const loadOptInContacts = useCallback(async () => {
    setLoadingOptIn(true);
    try {
      setOptInContacts(await getChannelOptInList(orgId));
    } catch (err) {
      showToast('Failed to load opt-in list: ' + err.message, 'error');
    } finally {
      setLoadingOptIn(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (activeTab === 'optin') loadOptInContacts();
  }, [activeTab, loadOptInContacts]);

  useEffect(() => {
    if (!orgId) return;
    seedDefaultTemplates(orgId).then(() => {
      loadCampaigns();
      loadTemplates();
    });
    fetchDistinctFieldValues('members', orgId, 'status').then(v => setMemberStatuses(v));
    fetchDistinctFieldValues('members', orgId, 'district').then(v => setMemberDistricts(v));
    fetchDistinctFieldValues('volunteers', orgId, 'status').then(v => setVolunteerStatuses(v));
    fetchDistinctFieldValues('volunteers', orgId, 'district').then(v => setVolunteerDistricts(v));
    getOrgName(orgId).then(n => setPreviewOrgName(n));
    if (adminId) getStaffDisplayName(orgId, adminId).then(n => { if (n) setPreviewStaffName(n); });
  }, [orgId, loadCampaigns, loadTemplates]);

  // ── Audience count ────────────────────────────────────────────────────────
  const refreshAudience = useCallback(async (targetingType, audienceFilters, selectedContacts) => {
    if (!orgId) return;
    setAudienceLoading(true);
    try {
      const at = derivedAudienceType(targetingType);
      const filters = targetingType === 'specific_contacts'
        ? { specificContactIds: selectedContacts.map(c => c.id) }
        : audienceFilters;
      const r = await resolveAudience(orgId, at, filters, 'push');
      setAudienceResult(r);
    } catch (err) {
      console.error('resolveAudience:', err);
    } finally {
      setAudienceLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (activeTab === 'create') {
      refreshAudience(form.targetingType, form.audienceFilters, form.selectedContacts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, form.targetingType, JSON.stringify(form.audienceFilters), JSON.stringify(form.selectedContacts)]);

  // ── Contact search (debounced) ────────────────────────────────────────────
  useEffect(() => {
    if (!contactSearch.trim() || form.targetingType !== 'specific_contacts') {
      setContactResults([]);
      return;
    }
    setSearchingContacts(true);
    const timer = setTimeout(async () => {
      try {
        const results = await searchContacts(orgId, contactSearch, 'both');
        const selectedIds = new Set(form.selectedContacts.map(c => c.id));
        setContactResults(results.filter(c => !selectedIds.has(c.id)));
      } catch {
        setContactResults([]);
      } finally {
        setSearchingContacts(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [contactSearch, orgId, form.targetingType, form.selectedContacts]);

  // ── Template auto-fill ────────────────────────────────────────────────────
  const handleTemplateSelect = (templateId) => {
    const tpl = templates.find(t => t.id === templateId);
    if (tpl) {
      setForm(f => ({
        ...f,
        templateId,
        name: f.name || tpl.name || '',
        tplCategory: tpl.category || '',
        title: tpl.title || f.title,
        body: tpl.body || f.body,
        targetingType: tpl.audience === 'volunteers' ? 'all_volunteers' : tpl.audience === 'members' ? 'all_members' : 'all_contacts',
      }));
    } else {
      setF('templateId', templateId);
    }
  };

  // ── Image URL validation ──────────────────────────────────────────────────
  const validateImageUrl = (url) => {
    if (!url) { setImageError(''); setImagePreview(''); return true; }
    try { new URL(url); } catch { setImageError('Please enter a valid URL'); setImagePreview(''); return false; }
    const urlLower = url.toLowerCase();
    const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
    const commonImageHosts = ['imgur.com', 'giphy.com', 'tenor.com', 'cloudinary.com', 'imagekit.io'];
    const hasValidExtension = validExtensions.some(ext => urlLower.includes(ext));
    const hasImageHost = commonImageHosts.some(host => urlLower.includes(host));
    if (!hasValidExtension && !hasImageHost) {
      setImageError('URL should point to an image (jpg, png, gif, etc.)');
      setImagePreview('');
      return false;
    }
    setImageError('');
    setImagePreview(url);
    return true;
  };
  const handleImageUrlChange = (value) => {
    setF('imageUrl', value);
    const id = setTimeout(() => validateImageUrl(value), 500);
    return () => clearTimeout(id);
  };
  const clearImage = () => { setF('imageUrl', ''); setImagePreview(''); setImageError(''); };

  // ── Emoji insert ──────────────────────────────────────────────────────────
  const insertEmoji = (field, emoji) => {
    if (field === 'title') {
      const el = titleRef.current;
      if (!el) { setF('title', form.title + emoji); return; }
      const start = el.selectionStart; const end = el.selectionEnd;
      const next = form.title.slice(0, start) + emoji + form.title.slice(end);
      setF('title', next);
      setTimeout(() => { el.focus(); el.setSelectionRange(start + emoji.length, start + emoji.length); }, 0);
    } else {
      const el = bodyRef.current;
      if (!el) { setF('body', form.body + emoji); return; }
      const start = el.selectionStart; const end = el.selectionEnd;
      const next = form.body.slice(0, start) + emoji + form.body.slice(end);
      setF('body', next);
      setTimeout(() => { el.focus(); el.setSelectionRange(start + emoji.length, start + emoji.length); }, 0);
    }
  };

  // ── Save draft ────────────────────────────────────────────────────────────
  const handleSaveDraft = async () => {
    if (!form.name.trim()) { showToast('Campaign name is required.', 'error'); return; }
    setFormSaving(true);
    try {
      const at = derivedAudienceType(form.targetingType);
      const filters = form.targetingType === 'specific_contacts'
        ? { specificContactIds: form.selectedContacts.map(c => c.id) }
        : form.audienceFilters;
      const payload = { name: form.name, templateId: form.templateId, title: form.title, body: form.body, imageUrl: form.imageUrl, deepLinkUrl: form.deepLinkUrl, audienceType: at, audienceFilters: filters, customMergeValues: form.customMergeValues || {} };
      if (editingId) {
        await updatePushCampaign(orgId, editingId, payload);
      } else {
        const newId = await createPushCampaign(orgId, payload, adminId);
        setEditingId(newId);
      }
      showToast('Draft saved.');
      loadCampaigns();
    } catch (err) {
      showToast('Save failed: ' + err.message, 'error');
    } finally {
      setFormSaving(false);
    }
  };

  // ── Open send confirm ─────────────────────────────────────────────────────
  const handleOpenSend = async (action) => {
    if (!form.name || !form.title || !form.body) { showToast('Name, title, and body are required.', 'error'); return; }
    if (form.title.length > PUSH_CHAR_LIMITS.title) { showToast(`Title exceeds ${PUSH_CHAR_LIMITS.title} character limit.`, 'error'); return; }
    if (form.body.length > PUSH_CHAR_LIMITS.bodyAndroid) { showToast(`Body exceeds Android limit of ${PUSH_CHAR_LIMITS.bodyAndroid} characters.`, 'error'); return; }
    if (action === 'schedule' && !form.scheduleType === 'schedule' && !form.scheduledAt) { showToast('Please select a scheduled date/time.', 'error'); return; }
    await refreshAudience(form.targetingType, form.audienceFilters, form.selectedContacts);
    setSendAction(action);
    setShowSendConfirm(true);
  };

  // ── Confirm send ──────────────────────────────────────────────────────────
  const handleConfirmSend = async () => {
    setSendingCampaign(true);
    try {
      const at = derivedAudienceType(form.targetingType);
      const filters = form.targetingType === 'specific_contacts'
        ? { specificContactIds: form.selectedContacts.map(c => c.id) }
        : form.audienceFilters;
      const payload = { name: form.name, templateId: form.templateId, title: form.title, body: form.body, imageUrl: form.imageUrl, deepLinkUrl: form.deepLinkUrl, audienceType: at, audienceFilters: filters, customMergeValues: form.customMergeValues || {} };
      let cId = editingId;
      if (!cId) {
        cId = await createPushCampaign(orgId, payload, adminId);
        setEditingId(cId);
      } else {
        await updatePushCampaign(orgId, cId, payload);
      }
      const eligible = audienceResult?.eligibleCount || 0;
      if (sendAction === 'now') {
        // People over the frequency limit are left out; everything else (opt-ins, tokens) is decided by the server.
        const { frequencyExcludedCustomers = [] } = await getFinalContactList(
          orgId, 'push', { audienceType: at, audienceFilters: filters }, null
        );
        const r = await sendPushCampaignNow(orgId, cId, frequencyExcludedCustomers.map(c => c.id));
        const { sent = 0, failed = 0, no_device: noDevice = 0 } = r.counts || {};
        if (r.status === 'sent') {
          showToast(`Delivered to ${sent} recipient(s)` + (failed + noDevice > 0 ? `; ${failed + noDevice} could not be reached (open the campaign for reasons).` : '.'),
            failed + noDevice > 0 ? 'error' : undefined);
        } else {
          showToast('Nothing was delivered: ' + (r.failureReason || 'see campaign details.'), 'error');
        }
      } else {
        await schedulePushCampaign(orgId, cId, form.scheduledAt, eligible);
        showToast('Campaign scheduled.');
      }
      setShowSendConfirm(false);
      resetForm();
      await loadCampaigns();
      setActiveTab('create');
    } catch (err) {
      showToast('Failed: ' + err.message, 'error');
    } finally {
      setSendingCampaign(false);
    }
  };

  // ── Table actions ─────────────────────────────────────────────────────────
  const handleEdit = (c) => {
    setForm({
      ...emptyForm(),
      name: c.name || '', templateId: c.templateId || '',
      title: c.title || '', body: c.body || '',
      imageUrl: c.imageUrl || '', deepLinkUrl: c.deepLinkUrl || '',
      targetingType: c.targetingType || 'all_contacts',
      audienceFilters: c.audienceFilters || {},
      customMergeValues: c.customMergeValues || {},
    });
    setEditingId(c.id);
    setAudienceResult(null);
    setActiveTab('create');
  };

  const resetForm = () => {
    setForm(emptyForm());
    setEditingId(null);
    setAudienceResult(null);
    setSavedTargeting(null);
    setContactSearch('');
    setContactResults([]);
  };

  const handleDuplicate = async (c) => {
    try {
      await duplicatePushCampaign(orgId, c.id, adminId);
      showToast('Campaign duplicated.');
      loadCampaigns();
    } catch (err) {
      showToast('Duplicate failed: ' + err.message, 'error');
    }
  };

  const handleCancel = async (c) => {
    if (!window.confirm(`Cancel campaign "${c.name}"?`)) return;
    try {
      await cancelPushCampaign(orgId, c.id);
      showToast('Campaign cancelled.');
      loadCampaigns();
    } catch (err) {
      showToast('Cancel failed: ' + err.message, 'error');
    }
  };

  // ── Filtered & paginated ──────────────────────────────────────────────────
  const filtered = campaigns.filter(c => {
    if (statusFilter && c.status !== statusFilter) return false;
    if (search && !(c.name || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ── Template CRUD ─────────────────────────────────────────────────────────
  const handleTplSave = async (data, existing) => {
    setTplSaving(true);
    try {
      if (existing) {
        await updatePushTemplate(orgId, existing.id, data);
      } else {
        await createPushTemplate(orgId, data, adminId);
      }
      showToast('Template saved.');
      setTplEditingId(null);
      loadTemplates();
    } catch (err) {
      showToast('Save failed: ' + err.message, 'error');
    } finally {
      setTplSaving(false);
    }
  };

  const handleUseTemplate = (tpl) => {
    setForm(f => ({
      ...f,
      templateId: tpl.id,
      tplCategory: tpl.category || '',
      title: tpl.title || f.title,
      body: tpl.body || f.body,
      targetingType: tpl.audience === 'volunteers' ? 'all_volunteers' : tpl.audience === 'members' ? 'all_members' : 'all_contacts',
    }));
    setEditingId(null);
    setAudienceResult(null);
    setActiveTab('create');
  };

  // Template filter counts
  const tplCounts = { all: templates.length, triggered: templates.filter(t => (t.category || 'triggered') === 'triggered').length, adhoc: templates.filter(t => t.category === 'adhoc').length };
  const visibleTemplates = tplCategoryFilter === 'all' ? templates : templates.filter(t => (t.category || 'triggered') === tplCategoryFilter);

  // char-counter helpers
  const titleOver  = form.title.length > PUSH_CHAR_LIMITS.title;
  const bodyOverIOS = form.body.length > PUSH_CHAR_LIMITS.bodyIOS;
  const bodyOverAndroid = form.body.length > PUSH_CHAR_LIMITS.bodyAndroid;

  const eligibleCount = audienceResult?.eligibleCount ?? 0;

  // ── Opt-In management ─────────────────────────────────────────────────────
  const filteredOptIn = optInContacts.filter(c => {
    const optIn = getContactOptIn(c, 'push');
    if (optInFilter === 'opted_in' && !optIn) return false;
    if (optInFilter === 'opted_out' && optIn) return false;
    if (optInSearch) {
      const q = optInSearch.toLowerCase();
      return (c.displayName || c.name || '').toLowerCase().includes(q) ||
             (c.email || '').toLowerCase().includes(q);
    }
    return true;
  });
  const optInTotalPages = Math.max(1, Math.ceil(filteredOptIn.length / PAGE_SIZE));
  const pagedOptIn = filteredOptIn.slice((optInPage - 1) * PAGE_SIZE, optInPage * PAGE_SIZE);

  const handleOptInUpdate = async (contact, newOptIn) => {
    if (!optInReason.trim()) { showToast('Please enter a reason before updating.', 'error'); return; }
    setUpdatingOptIn(contact.id);
    try {
      await updateChannelOptIn(orgId, contact.id, contact._collection, 'push', newOptIn, adminId, optInReason);
      showToast(`Push opt-in updated for ${contact.displayName || contact.name}.`);
      setShowOptInReasonFor(null);
      setOptInReason('');
      loadOptInContacts();
    } catch (err) {
      showToast('Update failed: ' + err.message, 'error');
    } finally {
      setUpdatingOptIn(null);
    }
  };

  const handleExportOptInCSV = () => {
    const csv = exportChannelOptInCSV(filteredOptIn, 'push');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = 'push-optin-list.csv'; link.click();
    URL.revokeObjectURL(url);
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="bb-comm-root">

      {/* Sub-tab bar */}
      <div className="bb-comm-subtab-bar">
        {[
          { key: 'create',    label: editingId ? 'Edit Campaign' : 'Create Campaign' },
          { key: 'templates', label: 'Templates' },
          { key: 'optin',     label: 'Opt-In Management' },
          { key: 'frequency', label: 'Campaign Frequency' },
        ].map(t => (
          <button
            key={t.key}
            className={`bb-comm-subtab${activeTab === t.key ? ' active' : ''}`}
            onClick={() => { if (t.key !== 'create') resetForm(); setActiveTab(t.key); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ─── CREATE / EDIT TAB ───────────────────────────────────────────── */}
      {activeTab === 'create' && (
        <div className="bb-comm-campaign-form">
          <div className="bb-comm-section-header" style={{ marginBottom: 20 }}>
            <h3 className="bb-comm-section-title">{editingId ? 'Edit Push Campaign' : 'Create Push Campaign'}</h3>
          </div>

          {/* Campaign Details */}
          <div className="bb-comm-section-card">
            <div className="bb-comm-card-title">Campaign Details</div>
            <div className="bb-form-group">
              <label className="bb-form-label">Campaign Name *</label>
              <input className="bb-form-input" value={form.name} onChange={e => setF('name', e.target.value)} placeholder="e.g. Volunteer Shift Alert — May 2026" />
            </div>
            <div className="bb-form-group">
              <label className="bb-form-label">Start from Template <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(optional)</span></label>
              <select className="bb-form-select" value={form.templateId} onChange={e => handleTemplateSelect(e.target.value)}>
                <option value="">— Select a template —</option>
                <optgroup label="Triggered Campaigns">
                  {templates.filter(t => (t.category || 'triggered') === 'triggered').map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </optgroup>
                <optgroup label="AdHoc Campaigns">
                  {templates.filter(t => t.category === 'adhoc').map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </optgroup>
              </select>
            </div>
          </div>

          {/* Notification Content */}
          <div className="bb-comm-section-card">
            <div className="bb-comm-card-title">Notification Content</div>

            <div className="bb-form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <label className="bb-form-label" style={{ marginBottom: 0 }}>
                  Notification Title *{' '}
                  <span style={{ color: '#9CA3AF', fontWeight: 400, fontSize: 11 }}>max {PUSH_CHAR_LIMITS.title} chars</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <button type="button" style={{ background: 'none', border: '1px solid #E5E7EB', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 13 }} onClick={() => { setShowTitleEmoji(v => !v); setShowBodyEmoji(false); }}>😊</button>
                  {showTitleEmoji && <EmojiPicker onSelect={e => insertEmoji('title', e)} onClose={() => setShowTitleEmoji(false)} />}
                </div>
              </div>
              <input
                ref={titleRef}
                className="bb-form-input"
                style={titleOver ? { borderColor: '#EF4444' } : {}}
                value={form.title}
                onChange={e => setF('title', e.target.value)}
                placeholder="e.g. New Shift Available"
              />
              <div style={{ marginTop: 4 }}>
                <CharCounter value={form.title} limit={PUSH_CHAR_LIMITS.title} label="Title" />
              </div>
            </div>

            <div className="bb-form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <label className="bb-form-label" style={{ marginBottom: 0 }}>Notification Body *</label>
                <div style={{ position: 'relative' }}>
                  <button type="button" style={{ background: 'none', border: '1px solid #E5E7EB', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 13 }} onClick={() => { setShowBodyEmoji(v => !v); setShowTitleEmoji(false); }}>😊</button>
                  {showBodyEmoji && <EmojiPicker onSelect={e => insertEmoji('body', e)} onClose={() => setShowBodyEmoji(false)} />}
                </div>
              </div>
              <div className="bb-comm-merge-fields" style={{ marginBottom: 6 }}>
                <span style={{ fontSize: 11, color: '#9CA3AF', marginRight: 4 }}>Insert:</span>
                {[{ tag: '{{firstName}}', label: 'First Name' }, { tag: '{{fullName}}', label: 'Full Name' }].map(f => (
                  <button key={f.tag} className="bb-comm-merge-btn" type="button" onClick={() => insertEmoji('body', f.tag)}>{f.label}</button>
                ))}
              </div>
              <textarea
                ref={bodyRef}
                className="bb-form-input"
                style={{ minHeight: 100, fontFamily: 'inherit', fontSize: 13, ...(bodyOverAndroid ? { borderColor: '#EF4444' } : bodyOverIOS ? { borderColor: '#F59E0B' } : {}) }}
                value={form.body}
                onChange={e => setF('body', e.target.value)}
                placeholder="e.g. A volunteer shift on {{shiftDate}} just opened. Sign up now!"
              />
              <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
                <CharCounter value={form.body} limit={PUSH_CHAR_LIMITS.bodyIOS} label="iOS limit" />
                <CharCounter value={form.body} limit={PUSH_CHAR_LIMITS.bodyAndroid} label="Android limit" />
              </div>
            </div>

            <div className="bb-form-group">
              <label className="bb-form-label">
                Hero Image/GIF URL <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(Optional)</span>
                {imageError && <span style={{ color: '#EF4444', fontWeight: 400, marginLeft: 6 }}>— {imageError}</span>}
              </label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  type="url"
                  className="bb-form-input"
                  style={{ paddingRight: form.imageUrl ? 36 : undefined, ...(imageError ? { borderColor: '#EF4444' } : {}) }}
                  value={form.imageUrl}
                  onChange={e => handleImageUrlChange(e.target.value)}
                  placeholder="https://example.com/image.jpg or https://giphy.com/..."
                />
                {form.imageUrl && (
                  <button
                    type="button"
                    onClick={clearImage}
                    title="Clear image"
                    style={{ position: 'absolute', right: 10, background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: 14, lineHeight: 1, padding: 2 }}
                  >✕</button>
                )}
              </div>
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                Add a banner image or GIF. Paste a direct link to a jpg/png or a Giphy/Tenor GIF URL.
              </div>
              <div style={{ fontSize: 11, color: '#5d4037', marginTop: 6, padding: '8px 10px', background: '#fff8e1', borderRadius: 6, borderLeft: '3px solid #f9a825' }}>
                <strong>GIF animation note:</strong> When the customer has the app open, GIFs play with full animation in an in-app popup. When the app is closed, the OS notification banner always shows only the first frame — this is a system-level limitation on all devices and cannot be changed.
              </div>
              {imagePreview && !imageError && (
                <div style={{ marginTop: 8, borderRadius: 6, overflow: 'hidden', background: '#F3F4F6', lineHeight: 0 }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{ maxWidth: '100%', height: 'auto', maxHeight: 160, display: 'block', objectFit: 'cover' }}
                    onError={() => { setImageError('Failed to load image. Please check the URL.'); setImagePreview(''); }}
                  />
                </div>
              )}
            </div>

            <div className="bb-form-group">
              <label className="bb-form-label">Deep Link URL <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(optional)</span></label>
              <input
                className="bb-form-input"
                value={form.deepLinkUrl}
                onChange={e => setF('deepLinkUrl', e.target.value)}
                placeholder="e.g. bespokebridge://shifts/upcoming"
              />
            </div>

            <MessagePreviewPane
              title={form.title}
              body={form.body}
              imageUrl={form.imageUrl}
              orgName={previewOrgName}
              previewName={previewStaffName}
              customMergeValues={form.customMergeValues}
            />
          </div>

          {/* Audience */}
          <div className="bb-comm-section-card">
            <div className="bb-comm-card-title">Audience</div>
            <AudienceFiltersSection
              targetingType={form.targetingType}
              audienceFilters={form.audienceFilters}
              selectedContacts={form.selectedContacts}
              contactSearch={contactSearch}
              contactResults={contactResults}
              searchingContacts={searchingContacts}
              onTargetingChange={v => { setF('targetingType', v); setContactSearch(''); setContactResults([]); }}
              onFiltersChange={v => setF('audienceFilters', v)}
              onContactSearch={setContactSearch}
              onAddContact={c => {
                if (!form.selectedContacts.find(x => x.id === c.id)) {
                  setF('selectedContacts', [...form.selectedContacts, c]);
                }
                setContactSearch('');
                setContactResults([]);
              }}
              onRemoveContact={id => setF('selectedContacts', form.selectedContacts.filter(c => c.id !== id))}
              memberStatuses={memberStatuses}
              memberDistricts={memberDistricts}
              volunteerStatuses={volunteerStatuses}
              volunteerDistricts={volunteerDistricts}
            />
            {/* Save Targeting Criteria */}
            {(() => {
              const currentKey = JSON.stringify({ targetingType: form.targetingType, audienceFilters: form.audienceFilters, selectedContacts: form.selectedContacts });
              const savedKey = savedTargeting ? JSON.stringify({ targetingType: savedTargeting.targetingType, audienceFilters: savedTargeting.audienceFilters, selectedContacts: savedTargeting.selectedContacts }) : null;
              const isDirty = savedTargeting && currentKey !== savedKey;
              return (
                <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="bb-btn-primary"
                    style={{ fontSize: 13, padding: '7px 18px' }}
                    onClick={() => setSavedTargeting({ targetingType: form.targetingType, audienceFilters: form.audienceFilters, selectedContacts: form.selectedContacts })}
                  >
                    💾 Save Targeting Criteria
                  </button>
                  {savedTargeting && !isDirty && (
                    <span style={{ fontSize: 12, color: '#065F46', background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: 12, padding: '3px 10px', fontWeight: 600 }}>
                      ✓ Criteria saved
                    </span>
                  )}
                  {isDirty && (
                    <span style={{ fontSize: 12, color: '#92400E', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 12, padding: '3px 10px', fontWeight: 600 }}>
                      ⚠ Modified — save again to update
                    </span>
                  )}
                </div>
              );
            })()}

            <div className="bb-comm-audience-summary" style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
              {audienceLoading
                ? 'Calculating…'
                : audienceResult !== null
                  ? <>
                      <span>
                        <span style={{ fontWeight: 700, color: '#4338CA' }}>{eligibleCount}</span>
                        {' FCM-eligible recipients'}
                      </span>
                      {eligibleCount > 0 && savedTargeting && (
                        <button className="bb-comm-link-btn" onClick={() => setShowRecipientPreview(true)}>
                          Preview Recipients →
                        </button>
                      )}
                    </>
                  : null}
            </div>
          </div>

          {/* Send Options */}
          <div className="bb-comm-section-card">
            <div className="bb-comm-card-title">Send Options</div>
            <div className="bb-comm-radio-group" style={{ marginBottom: 12 }}>
              <label className="bb-comm-schedule-option">
                <input type="radio" name="scheduleType" value="now" checked={form.scheduleType === 'now'} onChange={() => { setF('scheduleType', 'now'); setF('recurring', false); }} />
                Send Now
              </label>
              <label className="bb-comm-schedule-option">
                <input type="radio" name="scheduleType" value="schedule" checked={form.scheduleType === 'schedule' && !form.recurring} onChange={() => { setF('scheduleType', 'schedule'); setF('recurring', false); }} />
                Schedule for Later
              </label>
              <label className="bb-comm-schedule-option">
                <input type="radio" name="scheduleType" value="recurring" checked={form.recurring} onChange={() => { setF('scheduleType', 'recurring'); setF('recurring', true); }} />
                Recurring
              </label>
            </div>
            {form.scheduleType === 'schedule' && !form.recurring && (
              <div className="bb-form-group">
                <label className="bb-form-label">Scheduled Date & Time <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(Singapore Time)</span></label>
                <input
                  type="datetime-local"
                  className="bb-form-input"
                  style={{ maxWidth: 280 }}
                  value={form.scheduledAt}
                  onChange={e => setF('scheduledAt', e.target.value)}
                />
              </div>
            )}
            <RecurringSection form={form} onChange={v => setForm(v)} />
          </div>

          {/* Merge Field Values */}
          <MergeFieldValuesSection
            values={form.customMergeValues || {}}
            onChange={vals => setF('customMergeValues', vals)}
            body={form.body + ' ' + form.title}
          />

          {/* Final Contacts Targeted */}
          <FinalCustomersTargeted
            orgId={orgId}
            campaignType="push"
            formData={savedTargeting ? { ...form, targetingType: savedTargeting.targetingType, audienceFilters: savedTargeting.audienceFilters, selectedContacts: savedTargeting.selectedContacts } : form}
            disabled={!savedTargeting}
          />

          {/* Actions */}
          <div className="bb-comm-form-actions">
            <button className="bb-btn-secondary" onClick={handleSaveDraft} disabled={formSaving}>
              {formSaving ? 'Saving…' : 'Save Draft'}
            </button>
            <button
              className="bb-btn-primary"
              onClick={() => handleOpenSend(form.scheduleType === 'schedule' && !form.recurring ? 'schedule' : 'now')}
              disabled={formSaving}
            >
              {form.recurring ? 'Activate Recurring' : form.scheduleType === 'schedule' ? 'Schedule Campaign' : 'Send Campaign'}
            </button>
            <button className="bb-btn-ghost" type="button" onClick={() => { resetForm(); setActiveTab('create'); }}>
              Discard
            </button>
          </div>
        </div>
      )}

      {/* ─── TEMPLATES TAB ───────────────────────────────────────────────── */}
      {activeTab === 'templates' && (
        <div>
          <div className="bb-comm-section-header">
            <h3 className="bb-comm-section-title">Push Templates</h3>
            <button className="bb-btn-primary" onClick={() => setTplEditingId('new')}>+ New Template</button>
          </div>

          {/* Campaign type filter tabs */}
          <div className="bb-comm-category-tabs" style={{ marginBottom: 16 }}>
            {[
              { key: 'all',       label: `All (${tplCounts.all})` },
              { key: 'triggered', label: `Triggered Campaigns (${tplCounts.triggered})` },
              { key: 'adhoc',     label: `AdHoc Campaigns (${tplCounts.adhoc})` },
            ].map(tab => (
              <button key={tab.key} className={`bb-comm-category-tab${tplCategoryFilter === tab.key ? ' active' : ''}`} onClick={() => setTplCategoryFilter(tab.key)}>
                {tab.label}
              </button>
            ))}
          </div>

          {tplEditingId === 'new' && (
            <div className="bb-comm-card" style={{ marginBottom: 16 }}>
              <div className="bb-comm-card-title">Create New Template</div>
              <TemplateEditorForm
                initial={null}
                onSave={data => handleTplSave(data, null)}
                onCancel={() => setTplEditingId(null)}
                saving={tplSaving}
              />
            </div>
          )}

          {visibleTemplates.length === 0 ? (
            <div className="bb-comm-loading">No templates found.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="bb-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Template Name</th>
                    <th>Category</th>
                    <th>Trigger Context</th>
                    <th>Audience</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleTemplates.map(tpl => (
                    <React.Fragment key={tpl.id}>
                      <tr className={tplEditingId === tpl.id ? 'bb-comm-row--editing' : ''}>
                        <td className="bb-comm-cell--name">
                          {tpl.name}
                          {tpl.isDefault && <span className="bb-comm-badge bb-comm-badge--default">Default</span>}
                        </td>
                        <td>
                          <span className={`bb-comm-category-badge bb-comm-category-badge--${tpl.category || 'triggered'}`}>
                            {tpl.category === 'adhoc' ? 'AdHoc' : 'Triggered'}
                          </span>
                        </td>
                        <td className="bb-comm-cell--muted">{tpl.triggerContext || '—'}</td>
                        <td><AudienceLabel audienceType={tpl.audience} /></td>
                        <td className="bb-comm-cell--actions">
                          <button
                            className="bb-comm-action-btn"
                            onClick={() => handleUseTemplate(tpl)}
                          >
                            Use
                          </button>
                          {' '}
                          <button
                            className="bb-comm-action-btn"
                            onClick={() => setTplEditingId(tplEditingId === tpl.id ? null : tpl.id)}
                          >
                            {tplEditingId === tpl.id ? 'Cancel Edit' : 'Edit'}
                          </button>
                        </td>
                      </tr>
                      {tplEditingId === tpl.id && (
                        <tr>
                          <td colSpan={5} className="bb-comm-cell--editor">
                            <TemplateEditorForm
                              initial={tpl}
                              onSave={data => handleTplSave(data, tpl)}
                              onCancel={() => setTplEditingId(null)}
                              saving={tplSaving}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ─── OPT-IN MANAGEMENT TAB ───────────────────────────────────────── */}
      {activeTab === 'optin' && (
        <div style={{ padding: '24px 0' }}>
          <div className="bb-comm-section-header" style={{ marginBottom: 8 }}>
            <h3 className="bb-comm-section-title">Push Notification Opt-In Management</h3>
            <button className="bb-btn-secondary" onClick={handleExportOptInCSV} disabled={filteredOptIn.length === 0}>
              Export CSV
            </button>
          </div>
          <p style={{ color: '#6B7280', fontSize: 13, marginBottom: 16 }}>
            Members and volunteers manage their push notification subscription from their profile. Admins can manually override opt-in status here with a recorded reason for audit compliance.
          </p>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <input
              className="bb-form-input"
              style={{ maxWidth: 280 }}
              placeholder="Search by name or email…"
              value={optInSearch}
              onChange={e => { setOptInSearch(e.target.value); setOptInPage(1); }}
            />
            <select
              className="bb-form-input"
              style={{ maxWidth: 180 }}
              value={optInFilter}
              onChange={e => { setOptInFilter(e.target.value); setOptInPage(1); }}
            >
              <option value="">All Contacts</option>
              <option value="opted_in">Opted In</option>
              <option value="opted_out">Opted Out</option>
            </select>
          </div>
          {loadingOptIn ? (
            <div className="bb-comm-loading">Loading contacts…</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table className="bb-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Email</th>
                      <th>Opt-In Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedOptIn.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="bb-comm-empty-row">
                          {optInContacts.length === 0 ? 'No contacts found.' : 'No contacts match your filters.'}
                        </td>
                      </tr>
                    ) : pagedOptIn.map(contact => {
                      const isOptedIn = getContactOptIn(contact, 'push');
                      return (
                        <React.Fragment key={contact.id}>
                          <tr>
                            <td className="bb-comm-cell--name">{contact.displayName || contact.name || '—'}</td>
                            <td><span style={{ textTransform: 'capitalize', fontSize: 12, color: '#6B7280' }}>{contact._type}</span></td>
                            <td className="bb-comm-cell--muted">{contact.email || '—'}</td>
                            <td>
                              <span style={{
                                display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                                fontSize: 11, fontWeight: 700,
                                ...(isOptedIn
                                  ? { color: '#065F46', border: '1px solid #6EE7B7', background: '#D1FAE5' }
                                  : { color: '#6B7280', border: '1px solid #D1D5DB', background: '#F3F4F6' }),
                              }}>
                                {isOptedIn ? 'Opted In' : 'Opted Out'}
                              </span>
                            </td>
                            <td className="bb-comm-cell--actions">
                              <button
                                className="bb-comm-action-btn"
                                onClick={() => {
                                  setShowOptInReasonFor(contact.id === showOptInReasonFor ? null : contact.id);
                                  setOptInReason('');
                                }}
                              >
                                {showOptInReasonFor === contact.id ? 'Cancel' : 'Change Status'}
                              </button>
                            </td>
                          </tr>
                          {showOptInReasonFor === contact.id && (
                            <tr>
                              <td colSpan={5} style={{ padding: '12px 16px', background: '#F9FAFB' }}>
                                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                  <div className="bb-form-group" style={{ flex: 1, minWidth: 240, marginBottom: 0 }}>
                                    <label className="bb-form-label">Reason for change *</label>
                                    <input
                                      className="bb-form-input"
                                      value={optInReason}
                                      onChange={e => setOptInReason(e.target.value)}
                                      placeholder="e.g. Contact requested opt-out by phone"
                                    />
                                  </div>
                                  {isOptedIn ? (
                                    <button
                                      className="bb-comm-action-btn bb-comm-action-btn--danger"
                                      onClick={() => handleOptInUpdate(contact, false)}
                                      disabled={updatingOptIn === contact.id || !optInReason.trim()}
                                    >
                                      {updatingOptIn === contact.id ? '…' : 'Opt Out'}
                                    </button>
                                  ) : (
                                    <button
                                      className="bb-comm-action-btn"
                                      style={{ background: '#D1FAE5', color: '#065F46', borderColor: '#6EE7B7' }}
                                      onClick={() => handleOptInUpdate(contact, true)}
                                      disabled={updatingOptIn === contact.id || !optInReason.trim()}
                                    >
                                      {updatingOptIn === contact.id ? '…' : 'Opt In'}
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
                <button className="bb-btn-secondary" onClick={() => setOptInPage(p => Math.max(1, p - 1))} disabled={optInPage === 1}>Previous</button>
                <span style={{ color: '#6B7280' }}>Page {optInPage} of {optInTotalPages} ({filteredOptIn.length} contacts)</span>
                <button className="bb-btn-secondary" onClick={() => setOptInPage(p => Math.min(optInTotalPages, p + 1))} disabled={optInPage === optInTotalPages}>Next</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ─── CAMPAIGN FREQUENCY TAB ──────────────────────────────────────── */}
      {activeTab === 'frequency' && (
        <div style={{ maxWidth: 700, padding: '24px 0' }}>
          <div className="bb-comm-section-header" style={{ marginBottom: 20 }}>
            <h3 className="bb-comm-section-title">Campaign Frequency</h3>
          </div>
          <p style={{ color: '#6B7280', fontSize: 13, marginBottom: 20 }}>
            Control how often contacts can receive push notification campaigns to prevent
            over-communication and maintain healthy engagement rates.
          </p>
          <CampaignFrequencyControl orgId={orgId} campaignType="push" />
        </div>
      )}

      {/* ─── Modals ──────────────────────────────────────────────────────── */}
      {showSendConfirm && (
        <SendConfirmModal
          name={form.name}
          title={form.title}
          recipientCount={audienceResult?.eligibleCount ?? 0}
          scheduleMode={sendAction === 'schedule'}
          scheduleAt={form.scheduledAt}
          onConfirm={handleConfirmSend}
          onClose={() => setShowSendConfirm(false)}
          sending={sendingCampaign}
        />
      )}

      {viewCampaign && <ViewModal campaign={viewCampaign} onClose={() => setViewCampaign(null)} />}

      {showRecipientPreview && (
        <RecipientPreviewModal
          orgId={orgId}
          targetingType={form.targetingType}
          audienceFilters={form.audienceFilters}
          selectedContacts={form.selectedContacts}
          onClose={() => setShowRecipientPreview(false)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className={`bb-comm-toast bb-comm-toast--${toast.type}`} onClick={() => setToast(null)}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
